'use client';

import * as React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@purpose/ui/components/accordion/accordion';
import {Checkbox} from '@purpose/ui/components/checkbox/checkbox';
import {Category} from '../../types';
import {FiltersState} from '../../BenefitFiltersDrawer';

type CategoryState = Record<string, boolean>;

type CategoryTypesMap = Record<
  string,
  {
    id: number;
    titleId: string;
    icon: React.ElementType | null;
  }
>;

type CategoryTreeProps = {
  categories: Category[];
  categoriesTypesMap: CategoryTypesMap;
  t: (key: string) => string;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  filters: FiltersState;
};

const CategoryTree = ({
  categories,
  categoriesTypesMap,
  setFilters,
  filters,
  t
}: CategoryTreeProps) => {
  const [checked, setChecked] = React.useState<CategoryState>(
    (filters?.categories || []).reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {} as CategoryState)
  );

  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      categories: Object.entries(checked)
        .filter(([, isChecked]) => isChecked)
        .map(([id]) => Number(id))
    }));
  }, [checked, setFilters]);

  const toggleCategory = (id: number, value: boolean, category: Category) => {
    const updates: CategoryState = {};
    const traverse = (c: Category) => {
      updates[c.id] = value;
      c.children?.forEach(traverse);
    };
    traverse(category);
    setChecked(prev => ({...prev, ...updates}));
  };

  const getCategoryState = (
    category: Category
  ): 'checked' | 'unchecked' | 'indeterminate' => {
    if (!category.children?.length) {
      return checked[category.id] ? 'checked' : 'unchecked';
    }
    const childStates = category.children?.map(getCategoryState);
    const allChecked = childStates.every(s => s === 'checked');
    const noneChecked = childStates.every(s => s === 'unchecked');
    if (allChecked) return 'checked';
    if (noneChecked) return checked[category.id] ? 'checked' : 'unchecked';
    return 'indeterminate';
  };

  const renderCategory = (category: Category) => {
    const state = getCategoryState(category);
    const categoryFromMapper = categoriesTypesMap[category.key];
    const Icon = categoryFromMapper?.icon;

    return category.children?.length ? (
      <Accordion type="single" collapsible key={category.id} className="pl-2">
        <AccordionItem value={category.id.toString()} className="border-none">
          <AccordionTrigger className="flex items-center space-x-2 py-2">
            <div className="flex items-center">
              <Checkbox
                checked={state === 'checked' || state === 'indeterminate'}
                onCheckedChange={val =>
                  toggleCategory(category.id, !!val, category)
                }
                indeterminate={state === 'indeterminate'}
              />
              {Icon && <Icon className="mr-3 ml-6" />}
              <span className={Icon ? '' : 'ml-4'}>
                {t(categoryFromMapper?.titleId as string) || category.name}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-2 pl-6">
            {category.children?.map(renderCategory)}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    ) : (
      <div key={category.id} className="flex items-center py-2 pl-2">
        <Checkbox
          checked={!!checked[category.id]}
          onCheckedChange={val =>
            setChecked(prev => ({...prev, [category.id]: !!val}))
          }
          indeterminate={state === 'indeterminate'}
        />
        {Icon && <Icon className="ml-6" />}
        <span className="ml-3">
          {t(categoriesTypesMap[category?.key]?.titleId as string) ||
            category.name}
        </span>
      </div>
    );
  };

  return (
    <div className="max-h-[385px] space-y-2 overflow-scroll pr-4">
      {categories.map(renderCategory)}
    </div>
  );
};

export default CategoryTree;
